import { useState } from 'react'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { Plus, Send, ShieldAlert } from 'lucide-react'
import {
  getAdminAccountsListQueryKey,
  getV1MeRetrieveQueryOptions,
  useAdminAccountsInvitationResend,
  useAdminAccountsList,
} from '@/api/client'
import { AdminAccountsListRole, AdminAccountsListState } from '@/api/model'
import type { AdminAccountRow } from '@/api/model'
import { PageHeader } from '@/components/page-header'
import { AvatarPortrait } from '@/components/profile/dicebear-avatar'
import { avatarSeed } from '@/lib/avatar-catalog'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DataTable, type Column, type StateOption } from '@/components/data-table'
import { AccountStateBadge } from '@/components/admin-accounts/account-state-badge'
import { roleLabel } from '@/lib/admin-account-labels'
import { InviteDialog } from '@/components/admin-accounts/invite-dialog'
import { accountActionError } from '@/components/admin-accounts/account-error'
import { queryHasHttpError } from '@/lib/query-state'

export const Route = createFileRoute('/_panel/konta-administracyjne/')({
  beforeLoad: async ({ context }) => {
    // Superadmin-only domain (FR-002): everyone else is bounced out.
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role !== 'superadmin') {
      throw redirect({ to: '/' })
    }
  },
  component: AdminAccountsPage,
})

const PAGE_SIZE = 25
const ALL_ROLES = 'all'

const stateOptions: StateOption<AdminAccountsListState>[] = [
  { value: AdminAccountsListState.active, label: 'Aktywne' },
  { value: AdminAccountsListState.pending, label: 'Oczekujące' },
  { value: AdminAccountsListState.disabled, label: 'Wyłączone' },
  { value: AdminAccountsListState.all, label: 'Wszystkie' },
]

function AdminAccountsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [inviting, setInviting] = useState(false)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [state, setState] = useState<AdminAccountsListState>(AdminAccountsListState.active)
  const [role, setRole] = useState<string>(ALL_ROLES)
  // Per-invitation resend feedback, keyed by invitation id.
  const [resendNote, setResendNote] = useState<{ id: number; error: string | null } | null>(null)

  const resend = useAdminAccountsInvitationResend()

  const handleResend = async (invitationId: number) => {
    setResendNote(null)
    const res = await resend.mutateAsync({ invitationId })
    if (res.status >= 400) {
      setResendNote({ id: invitationId, error: accountActionError(res.data, res.status) })
      return
    }
    await queryClient.invalidateQueries({ queryKey: getAdminAccountsListQueryKey() })
    setResendNote({ id: invitationId, error: null })
  }

  const query = useAdminAccountsList({
    state,
    role: role === ALL_ROLES ? undefined : (role as AdminAccountsListRole),
    search: search.trim() || undefined,
    page,
    page_size: PAGE_SIZE,
  })
  const data = query.data?.status === 200 ? query.data.data : undefined

  // FR-015: the backend injects a top-level `superadmin_warning` onto the paginated
  // payload that is NOT part of the typed `PaginatedAdminAccountRowList` (research D2),
  // so we read it untyped.
  const superadminWarning =
    data && (data as { superadmin_warning?: boolean }).superadmin_warning === true

  const rows = data?.results ?? []

  const columns: Column<AdminAccountRow>[] = [
    {
      header: 'E-mail',
      cell: (r) => (
        <span className="flex items-center gap-2.5 font-medium">
          {/* alt pusty: adres stoi obok. `avatar` bywa nullowalne — wiersz
              zaproszenia nie ma jeszcze konta, więc zostaje placeholder. */}
          <AvatarPortrait options={r.avatar} seed={avatarSeed(r.email)} alt="" size="sm" />
          <span>
            {r.email}
            {r.nickname && (
              <span className="ml-1.5 text-xs text-muted-foreground">{r.nickname}</span>
            )}
          </span>
        </span>
      ),
    },
    {
      header: 'Rola',
      cell: (r) => <span className="text-muted-foreground">{roleLabel(r.role)}</span>,
    },
    {
      header: '2FA',
      cell: (r) => <span className="text-muted-foreground">{r.has_2fa ? 'Tak' : '—'}</span>,
    },
    { header: 'Stan', cell: (r) => <AccountStateBadge state={r.state} /> },
    {
      header: '',
      className: 'text-right',
      // US3: resend lives inline on the roster — pending invitations have no detail
      // route (their id is an Invitation pk, not a User pk).
      cell: (r) =>
        r.kind === 'invitation' ? (
          <div className="flex flex-col items-end gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={resend.isPending}
              onClick={(e) => {
                e.stopPropagation()
                void handleResend(r.id)
              }}
            >
              <Send /> Wyślij ponownie
            </Button>
            {resendNote?.id === r.id &&
              (resendNote.error ? (
                <span className="text-xs text-destructive">{resendNote.error}</span>
              ) : (
                <span className="text-xs text-green-600">Wysłano ponownie</span>
              ))}
          </div>
        ) : null,
    },
  ]

  return (
    <>
      <PageHeader
        title="Konta administracyjne"
        actions={
          <Button size="sm" onClick={() => setInviting(true)}>
            <Plus /> Zaproś konto
          </Button>
        }
      />
      {superadminWarning && (
        <div className="flex items-center gap-2 border-b border-amber-300 bg-amber-50 px-6 py-2.5 text-sm text-amber-900">
          <ShieldAlert className="size-4 shrink-0" />W systemie jest tylko jedno aktywne konto
          superadministratora. Zaproś kolejne, aby możliwe było odzyskanie dostępu.
        </div>
      )}
      <div className="p-6">
        <DataTable
          columns={columns}
          rows={rows}
          // id is unique only within a kind (User pk vs Invitation pk), so the key
          // must combine both to stay stable for account+invitation rows alike.
          rowKey={(r) => `${r.kind}:${r.id}`}
          count={data?.count ?? 0}
          page={page}
          onPageChange={setPage}
          pageSize={PAGE_SIZE}
          search={search}
          onSearchChange={(v) => {
            setSearch(v)
            setPage(1)
          }}
          searchPlaceholder="Szukaj po e-mailu lub nicku…"
          stateValue={state}
          stateOptions={stateOptions}
          onStateChange={(v) => {
            setState(v)
            setPage(1)
          }}
          filters={
            <Select
              value={role}
              onValueChange={(v) => {
                setRole(v)
                setPage(1)
              }}
            >
              <SelectTrigger size="sm" className="w-44">
                <SelectValue placeholder="Wszystkie role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_ROLES}>Wszystkie role</SelectItem>
                <SelectItem value={AdminAccountsListRole.superadmin}>
                  {roleLabel('superadmin')}
                </SelectItem>
                <SelectItem value={AdminAccountsListRole.admin}>{roleLabel('admin')}</SelectItem>
              </SelectContent>
            </Select>
          }
          isLoading={query.isLoading}
          isError={queryHasHttpError(query)}
          onRetry={() => void query.refetch()}
          emptyMessage="Brak kont spełniających kryteria."
          // Only activated accounts have a detail route (User pk); pending invitations
          // are managed inline from the roster.
          onRowClick={(r) =>
            r.kind === 'account'
              ? navigate({
                  to: '/konta-administracyjne/$kontoId',
                  params: { kontoId: String(r.id) },
                })
              : undefined
          }
          countNoun={['konto', 'kont']}
        />
      </div>

      <InviteDialog open={inviting} onOpenChange={setInviting} />
    </>
  )
}
